import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '@store/StoreProvider';
import { toastStore } from '@store/ToastStore';
import { AuthPageShell } from '@app/auth/AuthPageShell';

export default observer(function ForgotPasswordPage() {
  const { i18n } = useStore();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const clearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    let isValid = true;
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      newErrors.email = i18n.t('errors.auth.enter_email');
      isValid = false;
    } else if (!validateEmail(trimmedEmail)) {
      newErrors.email = i18n.t('errors.auth.invalid_email');
      isValid = false;
    }

    if (!isValid) {
      setErrors(newErrors);
      toastStore.show(i18n.t('errors.fix_form'), 'error');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${i18n.apiBaseUrl.replace(/\/$/, '')}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();
      if (response.ok) {
        toastStore.show(i18n.t('auth.success.reset_email_sent'), 'success');
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } else {
        const errorMessage = data.message || data.error || i18n.t('errors.generic');
        toastStore.show(errorMessage, 'error');
      }
    } catch (error) {
      toastStore.show(i18n.t('errors.server_connection'), 'error');
      console.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell title={i18n.t('auth.password.forgot')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            className={`fld ${errors.email ? 'fld-error' : ''}`}
            placeholder={i18n.t('auth.work_email_placeholder')}
            autoComplete="email"
            value={email}
            onChange={(ev) => {
              setEmail(ev.target.value);
              clearError('email');
            }}
          />
          {errors.email && <div className="auth-field-error">{errors.email}</div>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-grad w-full h-[52px] rounded-2xl font-bold text-white shadow-xl shadow-brand/30"
        >
          {isLoading ? i18n.t('app.status.sending') : i18n.t('app.actions.send')}
        </button>
      </form>

      <div className="mt-5 text-center text-sm text-slate-400 space-y-1.5">
        <div>
          {i18n.t('auth.go_to_login')}{' '}
          <Link to="/login" className="text-brand2 font-semibold hover:underline">
            {i18n.t('app.actions.sign_in')}
          </Link>
        </div>
        <div>
          {i18n.t('auth.register.cta')}{' '}
          <Link to="/register" className="text-brand2 font-semibold hover:underline">
            {i18n.t('auth.sign_up')}
          </Link>
        </div>
      </div>
    </AuthPageShell>
  );
});
